import { LayoutGrid, Rows3 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { usePool } from '@/components/layout/AppShell'
import { useGamesForWeek } from '@/features/games/queries'
import { useCompletedWeeks } from '@/features/standings/queries'
import { cn } from '@/lib/cn'
import PickCards from './PickCards'
import PickGrid from './PickGrid'
import { usePoolRoster, useWeekPicks } from './queries'

type View = 'grid' | 'cards'

const VIEW_KEY = 'side-action:week-board-view'
const WIDE = '(min-width: 768px)'

/* The default is responsive -- grid on a wide screen, cards on a phone -- but an
   explicit choice outranks it on every screen and survives a reload. Storing
   only an explicit choice is what keeps that true: persisting the resolved value
   would freeze whichever default the first visit happened to land on.

   Every access is wrapped: localStorage throws outright in some embedded
   webviews and with site data blocked, and a board that refuses to render
   because it could not remember a toggle is worse than one that forgets. */
function useBoardView() {
  const [chosen, setChosen] = useState<View | null>(() => {
    try {
      const stored = localStorage.getItem(VIEW_KEY)
      return stored === 'grid' || stored === 'cards' ? stored : null
    } catch {
      return null
    }
  })

  const [isWide, setIsWide] = useState(() => window.matchMedia(WIDE).matches)

  useEffect(() => {
    const query = window.matchMedia(WIDE)
    const onChange = (event: MediaQueryListEvent) => setIsWide(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const view: View = chosen ?? (isWide ? 'grid' : 'cards')

  const choose = (next: View) => {
    setChosen(next)
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch {
      // Preference is a convenience; losing it is not worth surfacing.
    }
  }

  return [view, choose] as const
}

export default function WeekBoardPage() {
  const pool = usePool()
  const [view, setView] = useBoardView()

  /* Locked weeks only. picks_select_pool_after_lock returns nothing before a
     week locks, so offering an open week would render an empty board that looks
     broken rather than private. useCompletedWeeks already filters on
     week_has_locked and orders newest first. */
  const { data: weeks, isPending: weeksPending } = useCompletedWeeks(pool.id)
  const [selected, setSelected] = useState<string | null>(null)
  const weekId = selected ?? weeks?.[0]?.week_id ?? undefined

  const { data: games, isPending: gamesPending } = useGamesForWeek(weekId, { live: true })
  const { data: picks, isPending: picksPending } = useWeekPicks(pool.id, weekId)
  const { data: members, isPending: rosterPending } = usePoolRoster(pool.id)

  const heading = (
    <header className="bg-card border-rule border-b px-6 py-5">
      <h1 className="font-display text-ink text-team">THE WEEK</h1>
      <p className="eyebrow mt-1.5">Every pick, once the week locks</p>
    </header>
  )

  if (weeksPending) {
    return (
      <>
        {heading}
        <p className="eyebrow px-6 py-8">Loading the week…</p>
      </>
    )
  }

  if (!weeks || weeks.length === 0) {
    return (
      <>
        {heading}
        <p className="text-ink-dim text-body px-6 py-8">
          Everyone&rsquo;s picks open up once the first week locks.
        </p>
      </>
    )
  }

  const loading = gamesPending || picksPending || rosterPending

  return (
    <>
      {heading}

      <div className="flex items-end gap-3 px-6 pt-5">
        <div className="min-w-0 flex-1">
          <label className="eyebrow block" htmlFor="week-board-week">
            Week
          </label>
          <select
            id="week-board-week"
            value={weekId ?? ''}
            onChange={(event) => setSelected(event.target.value)}
            className="bg-card border-rule-strong text-ink focus:border-field text-body mt-1.5 w-full border px-3 py-2.5 font-semibold"
          >
            {weeks.map((week) => (
              <option key={week.week_id} value={week.week_id ?? ''}>
                {week.week_label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex shrink-0" role="group" aria-label="Board layout">
          {(
            [
              { value: 'grid', label: 'Grid', Icon: LayoutGrid },
              { value: 'cards', label: 'Cards', Icon: Rows3 },
            ] as const
          ).map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setView(value)}
              aria-pressed={view === value}
              className={cn(
                'border-rule-strong flex items-center gap-1.5 border px-3 py-2.5 transition-colors',
                value === 'grid' ? 'border-r-0' : '-ml-px',
                view === value
                  ? 'bg-navy border-navy text-white'
                  : 'bg-card text-ink-dim hover:text-ink',
              )}
            >
              <Icon size={14} aria-hidden />
              <span className="text-eyebrow font-mono tracking-[0.14em] uppercase">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-5">
        {loading && <p className="eyebrow">Loading picks…</p>}

        {!loading && games && picks && members && (
          <>
            {games.length === 0 ? (
              <p className="text-ink-dim text-body">No games loaded for this week.</p>
            ) : picks.length === 0 ? (
              /* A locked week with no picks anywhere is a real state -- a pool
                 that forfeited en masse -- so this says so rather than blaming
                 the query. */
              <p className="text-ink-dim text-body">Nobody picked this week.</p>
            ) : view === 'grid' ? (
              <PickGrid games={games} picks={picks} members={members} tiePolicy={pool.tie_policy} />
            ) : (
              <PickCards
                games={games}
                picks={picks}
                members={members}
                tiePolicy={pool.tie_policy}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
